import React, { useState } from 'react';
import { 
  ClockIcon,
  PlayIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Backtest: React.FC = () => {
  const [selectedStrategy, setSelectedStrategy] = useState('grid');
  const [selectedPair, setSelectedPair] = useState('BTCUSDT');
  const [initialCapital, setInitialCapital] = useState(10000);
  const [feeRate, setFeeRate] = useState(0.1);
  const [dateRange, setDateRange] = useState({
    start: '2024-01-01',
    end: '2024-12-31'
  });
  const [backtestResults, setBacktestResults] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);

  const strategies = [
    { id: 'grid', name: '그리드 트레이딩' },
    { id: 'rsi', name: 'RSI 역추세' },
    { id: 'macd', name: 'MACD 크로스' },
    { id: 'ai', name: 'AI 예측 모델' }
  ];

  const tradingPairs = [
    'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'ADAUSDT', 'XRPUSDT'
  ];

  // 샘플 차트 데이터 생성
  const generateChartData = () => {
    const data = [];
    const startValue = initialCapital;
    let currentValue = startValue;
    
    for (let i = 0; i <= 365; i++) {
      const date = new Date('2024-01-01');
      date.setDate(date.getDate() + i);
      
      // 랜덤 변동성 추가 (전체적으로는 상승 추세)
      const dailyReturn = (Math.random() - 0.4) * 0.03; // -1.2% ~ +1.8% 일일 변동
      currentValue *= (1 + dailyReturn);
      
      data.push({
        date: date.toISOString().split('T')[0],
        value: Math.round(currentValue),
        benchmark: Math.round(startValue * (1 + (i / 365) * 0.15)) // 벤치마크: 연 15% 수익
      });
    }
    
    return data;
  };

  const runBacktest = () => {
    setIsRunning(true);
    
    // 실제 백테스트 시뮬레이션
    setTimeout(() => {
      const finalValue = initialCapital * 1.245; // 24.5% 수익
      const totalReturn = ((finalValue - initialCapital) / initialCapital * 100).toFixed(1);
      
      setBacktestResults({
        totalReturn: `+${totalReturn}%`,
        finalValue: `$${finalValue.toLocaleString()}`,
        sharpeRatio: 1.82,
        maxDrawdown: '-12.3%',
        winRate: '64%',
        totalTrades: 342,
        profitableTrades: 219,
        avgWin: '+1.2%',
        avgLoss: '-0.8%',
        profitFactor: 1.68,
        chartData: generateChartData()
      });
      setIsRunning(false);
    }, 3000);
  };

  const downloadReport = () => {
    const report = {
      strategy: selectedStrategy,
      tradingPair: selectedPair,
      period: `${dateRange.start} ~ ${dateRange.end}`,
      initialCapital: `$${initialCapital.toLocaleString()}`,
      results: backtestResults
    };
    
    const dataStr = JSON.stringify(report, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backtest-report-${selectedStrategy}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col p-4 lg:p-6">
      <div className="flex items-center mb-6">
        <ClockIcon className="h-6 w-6 mr-2 text-blue-500" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">백테스트</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 설정 패널 */}
        <div className="lg:col-span-1 bg-white dark:bg-dark-800 rounded-lg p-4 lg:p-6 border border-light-300 dark:border-dark-600">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">백테스트 설정</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">전략 선택</label>
              <select
                value={selectedStrategy}
                onChange={(e) => setSelectedStrategy(e.target.value)}
                className="w-full bg-white dark:bg-dark-700 border border-light-400 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {strategies.map(strategy => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">거래쌍</label>
              <select 
                value={selectedPair}
                onChange={(e) => setSelectedPair(e.target.value)}
                className="w-full bg-white dark:bg-dark-700 border border-light-400 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {tradingPairs.map(pair => (
                  <option key={pair} value={pair}>{pair}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">시작일</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  className="w-full bg-white dark:bg-dark-700 border border-light-400 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">종료일</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  className="w-full bg-white dark:bg-dark-700 border border-light-400 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">초기 자본 ($)</label>
              <input
                type="number"
                value={initialCapital}
                onChange={(e) => setInitialCapital(Number(e.target.value))}
                className="w-full bg-white dark:bg-dark-700 border border-light-400 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1000"
                max="1000000"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">수수료 (%)</label>
              <input
                type="number"
                value={feeRate}
                onChange={(e) => setFeeRate(Number(e.target.value))}
                className="w-full bg-white dark:bg-dark-700 border border-light-400 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="5"
                step="0.01"
              />
            </div>

            <button
              onClick={runBacktest}
              disabled={isRunning}
              className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                isRunning
                  ? 'bg-gray-400 dark:bg-gray-600 text-gray-700 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isRunning ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700 dark:border-gray-300 mr-2"></div>
                  백테스트 실행 중...
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5 mr-2" />
                  백테스트 시작
                </>
              )}
            </button>
          </div>
        </div>

        {/* 결과 패널 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 차트 영역 */}
          <div className="bg-white dark:bg-dark-800 rounded-lg p-4 lg:p-6 border border-light-300 dark:border-dark-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">수익률 차트</h3>
            <div className="h-64 lg:h-80">
              {backtestResults && backtestResults.chartData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={backtestResults.chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9CA3AF"
                      fontSize={12}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis 
                      stroke="#9CA3AF"
                      fontSize={12}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'rgb(55, 65, 81)',
                        border: '1px solid rgb(75, 85, 99)',
                        borderRadius: '8px',
                        color: 'rgb(255, 255, 255)'
                      }}
                      formatter={(value, name) => [
                        `$${Number(value).toLocaleString()}`,
                        name === 'value' ? '포트폴리오' : '벤치마크'
                      ]}
                      labelFormatter={(label) => `날짜: ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={false}
                      name="value"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="benchmark" 
                      stroke="#6B7280" 
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={false}
                      name="benchmark"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full bg-light-100 dark:bg-dark-700 rounded-lg flex items-center justify-center border border-light-300 dark:border-dark-600">
                  <div className="text-center">
                    <ChartBarIcon className="h-12 w-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-600 dark:text-gray-400 text-sm">백테스트를 실행하면 차트가 표시됩니다</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 성과 지표 */}
          {backtestResults && (
            <div className="bg-white dark:bg-dark-800 rounded-lg p-4 lg:p-6 border border-light-300 dark:border-dark-600">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">백테스트 결과</h3>
                <button 
                  onClick={downloadReport}
                  className="bg-light-200 dark:bg-dark-700 hover:bg-light-300 dark:hover:bg-dark-600 text-gray-900 dark:text-white px-4 py-2 rounded-lg flex items-center text-sm transition-colors border border-light-300 dark:border-dark-600"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  보고서 다운로드
                </button>
              </div>
              
              {/* 주요 지표 */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-6">
                <div className="bg-light-100 dark:bg-dark-700 rounded-lg p-3 lg:p-4 border border-light-300 dark:border-dark-600">
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600 dark:text-gray-400">총 수익률</span>
                    <CurrencyDollarIcon className="h-4 w-4 text-gray-500" />
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-green-600 dark:text-green-400 mt-1 lg:mt-2">{backtestResults.totalReturn}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{backtestResults.finalValue}</p>
                </div>

                <div className="bg-light-100 dark:bg-dark-700 rounded-lg p-3 lg:p-4 border border-light-300 dark:border-dark-600">
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600 dark:text-gray-400">샤프 비율</span>
                    <ChartBarIcon className="h-4 w-4 text-gray-500" />
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white mt-1 lg:mt-2">{backtestResults.sharpeRatio}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">위험 대비 수익</p>
                </div>

                <div className="bg-light-100 dark:bg-dark-700 rounded-lg p-3 lg:p-4 col-span-2 lg:col-span-1 border border-light-300 dark:border-dark-600">
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600 dark:text-gray-400">최대 손실</span>
                    <ArrowDownTrayIcon className="h-4 w-4 text-gray-500" />
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-red-600 dark:text-red-400 mt-1 lg:mt-2">{backtestResults.maxDrawdown}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">최대 낙폭</p>
                </div>
              </div>

              {/* 상세 통계 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-light-100 dark:bg-dark-700 rounded-lg p-4 border border-light-300 dark:border-dark-600">
                  <h4 className="text-gray-900 dark:text-white font-medium mb-3 text-sm lg:text-base">거래 통계</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">총 거래 수</span>
                      <span className="text-gray-900 dark:text-white">{backtestResults.totalTrades}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">수익 거래</span>
                      <span className="text-green-600 dark:text-green-400">{backtestResults.profitableTrades}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">손실 거래</span>
                      <span className="text-red-600 dark:text-red-400">{backtestResults.totalTrades - backtestResults.profitableTrades}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">승률</span>
                      <span className="text-gray-900 dark:text-white">{backtestResults.winRate}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-light-100 dark:bg-dark-700 rounded-lg p-4 border border-light-300 dark:border-dark-600">
                  <h4 className="text-gray-900 dark:text-white font-medium mb-3 text-sm lg:text-base">수익성 분석</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">평균 수익</span>
                      <span className="text-green-600 dark:text-green-400">{backtestResults.avgWin}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">평균 손실</span>
                      <span className="text-red-600 dark:text-red-400">{backtestResults.avgLoss}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">수익 팩터</span>
                      <span className="text-gray-900 dark:text-white">{backtestResults.profitFactor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Risk/Reward</span>
                      <span className="text-gray-900 dark:text-white">1:1.5</span>
                    </div>
                  </div>
                </div>

                <div className="bg-light-100 dark:bg-dark-700 rounded-lg p-4 border border-light-300 dark:border-dark-600">
                  <h4 className="text-gray-900 dark:text-white font-medium mb-3 text-sm lg:text-base">기간별 성과</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">월평균 수익</span>
                      <span className="text-green-600 dark:text-green-400">+2.04%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">최고 월 수익</span>
                      <span className="text-green-600 dark:text-green-400">+5.8%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">최악 월 수익</span>
                      <span className="text-red-600 dark:text-red-400">-2.1%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">수익 월 비율</span>
                      <span className="text-gray-900 dark:text-white">83%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Backtest; 